//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSelector } from '@reduxjs/toolkit';
import { LoadingStatuses } from '@/constants/constants';
import type { RootState } from '@/redux/store';

export const selectFileManager = (state: RootState) => state.fileManager;

// TODO: rewrite selectors using createSelector(selectFileManager, ...)
export const selectFileSource = (state: RootState) => selectFileManager(state)?.currentPath?.[0];
export const selectCurrentPath = (state: RootState) => selectFileManager(state)?.currentPath;
export const selectAllData = (state: RootState) => selectFileManager(state).data || [];
export const selectAllFileSources = (state: RootState) => selectFileManager(state)?.fileSources;
export const selectCurrentDepth = (state: RootState) => selectFileManager(state)?.currentDepth;
export const selectFolder = (state: RootState, depth) => selectAllData(state)?.[depth] || [];
export const selectFileManagerIsLoading = (state: RootState) => selectFileManager(state).status === LoadingStatuses.pending;
export const selectFolderIsLoading = (state: RootState) => !!selectFileManager(state).folderLoading || false;
export const selectNextPages = (state: RootState) => selectFileManager(state).nextPages;
export const selectLocationsLength = (state: RootState) => selectFileManager(state).locations.length;
export const selectLocationPointer = (state: RootState) => selectFileManager(state).locationPointer;
export const selectHasLoadedSourcesData = (state: RootState) => selectFileManager(state).hasLoadedSourcesData;

export const selectIsTransferQueueDrawerOpen = (state: RootState) => selectFileManager(state).isTransferQueueDrawerOpen || false;

export const selectCurrentFolder = (state: RootState) => {
  const depth = selectCurrentDepth(state) || 0;
  const data = selectAllData(state);
  return data[depth] || [];
};

export const selectHomePath = (state: RootState) => selectAllFileSources(state)?.[0]?.slug;
export const selectHomePathId = createSelector([selectCurrentPath, selectAllFileSources], (currentPath, fileSources) => {
  return fileSources?.find((source) => source.slug === currentPath[0])?.id;
});

export const selectConnectors = createSelector(selectFileManager, (fileManager) => fileManager?.connectors || {});
export const selectCurrentFolderConnectors = createSelector([selectConnectors, selectFileSource], (connectors, fileSource) => {
  return connectors[fileSource] || {};
});

export const selectCurrentPathString = createSelector(selectCurrentPath, (currentPath) => currentPath.filter((_, i) => i !== 0).join('/'));

export const selectActiveFileOrigin = createSelector([selectFileSource, selectAllFileSources], (fileSource, fileSources) => {
  return fileSources?.find((source) => source.slug === fileSource) || {};
});

export const selectFileOriginType = createSelector(selectActiveFileOrigin, (fileOrigin) => fileOrigin?.origin_type);

export const selectFileSearchParams = createSelector(selectFileManager, (fileManager) => fileManager?.searchParams || {});
export const selectFileResultsTotal = createSelector(selectFileManager, (fileManager) => fileManager?.pagination?.total || 0);

export const selectLastOpenedPendingAssetLoaction = createSelector(selectFileManager, (fileManager) => fileManager?.lastOpenedPendingAssetLoaction);
